import React, { useCallback, useEffect, useState } from "react";
import { MenuItem, Select, FormControl, InputLabel, Typography, Button, Dialog, DialogTitle, DialogContent, Box } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { apiPaths, getInstutueApp } from "../platform-components/api-integration-util";
import Grid from "../platform-components/grid/Grid";
import { getUserDetails } from "../platform-components/utils/shared-data.service";

const CONTENT_TYPES = [
  { value: "FLASHCARD", label: "Flashcards" },
  { value: "VIDEO", label: "Videos" },
  { value: "NOTES", label: "Notes" },
  { value: "QBANK_MODULE", label: "Question Bank Modules" },
  { value: "TEST_MODULE", label: "Test Modules" },
];

const iconStyle = {
  position: "absolute",
  top: "6px",
  right: "11px",
  fontSize: "16px",
  cursor: "pointer",
};

const StarRating = ({ value }) => {
  if (!value) return <span>-</span>;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((star) =>
        star <= value ? (
          <StarIcon key={star} style={{ color: "#f5a623", fontSize: 18 }} />
        ) : (
          <StarBorderIcon key={star} style={{ color: "#ccc", fontSize: 18 }} />
        )
      )}
    </div>
  );
};

const ContentFeedback = () => {
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contentType, setContentType] = useState("FLASHCARD");
  const [commentOpen, setCommentOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState({});

  const getHeaders = () => {
    const user = getUserDetails();
    return { tenantId: user.tenantId, userEmail: user.email };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getInstutueApp(
        apiPaths.getContentFeedback,
        { contentType },
        getHeaders()
      );
      setFeedbackData(response.result || []);
    } catch (error) {
      console.error("Error fetching content feedback:", error);
      setFeedbackData([]);
    } finally {
      setLoading(false);
    }
  }, [contentType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCommentOpen = (data) => {
    setSelectedComment(data);
    setCommentOpen(true);
  };

  const columnDefs = [
    {
      headerName: "Content Name",
      field: "contentName",
      tooltipField: "contentName",
      filter: true,
      flex: 2,
    },
    {
      headerName: "Student Email",
      field: "studentEmail",
      tooltipField: "studentEmail",
      filter: true,
      flex: 2,
    },
    {
      headerName: "Rating",
      field: "rating",
      filter: true,
      width: 150,
      cellRendererFramework: (params) => <StarRating value={params.value} />,
    },
    {
      headerName: "Feedback",
      field: "feedback",
      tooltipField: "feedback",
      filter: true,
      flex: 1,
      cellRendererFramework: (params) =>
        params.value ? (
          <Button
            size="small"
            onClick={() => handleCommentOpen(params.data)}
            style={{ cursor: "pointer" }}
          >
            View
          </Button>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        ),
    },
    {
      headerName: "Date",
      field: "updatedDate",
      tooltipField: "updatedDate",
      filter: true,
      width: 180,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
        <FormControl size="small" style={{ minWidth: 220 }}>
          <InputLabel>Content Type</InputLabel>
          <Select
            value={contentType}
            label="Content Type"
            onChange={(e) => setContentType(e.target.value)}
          >
            {CONTENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" style={{ color: "#666" }}>
          {!loading && `${feedbackData.length} feedback entries`}
        </Typography>
      </div>

      {loading && <p>Loading...</p>}
      {!loading && feedbackData.length > 0 && (
        <Grid
          gridOptions={{
            columnDefs,
            rowData: feedbackData,
            pagination: true,
            paginationPageSize: 10,
          }}
          parent="content-feedback"
        />
      )}
      {!loading && feedbackData.length === 0 && (
        <p style={{ color: "#999" }}>No feedback found for this content type.</p>
      )}

      <Dialog
        open={commentOpen}
        fullWidth
        onClose={() => setCommentOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle style={{ borderBottom: "1px solid gainsboro" }}>
          Feedback Details
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
            <CloseIcon sx={iconStyle} onClick={() => setCommentOpen(false)} />
            <Typography>
              <span style={{ fontWeight: "bold" }}>Content:</span>{" "}
              {selectedComment.contentName}
            </Typography>
            <Typography>
              <span style={{ fontWeight: "bold" }}>Email:</span>{" "}
              <span style={{ color: "#3366FF", fontWeight: "bold" }}>
                {selectedComment.studentEmail}
              </span>
            </Typography>
            <Typography>
              <span style={{ fontWeight: "bold" }}>Rating:</span>{" "}
              {selectedComment.rating ? `${selectedComment.rating}/5` : "N/A"}
            </Typography>
            <Typography style={{ marginTop: 8 }}>
              <span style={{ fontWeight: "bold" }}>Feedback:</span>{" "}
              {selectedComment.feedback || "No comments"}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentFeedback;
